// Financial Reporting suite — slide-in preview for a series instance (the
// shared engine PDF in an iframe over ResourceDrawer, same approach as
// LoanReportPreviewDrawer). The footer drives the PreparerReviewer ladder:
// Draft → "Submit for review" (Loans.Manage), PendingReview → "Certify &
// file" / Certified → "File" (FinancialReporting.Review — the second
// signature).

import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { loanReportsApi, type LoanReportDto } from "@/api/portfolioAdmin/loanReportsApi";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { formatDate } from "@/components/portfolioAdmin/loanDetail/common/colorBand";
import { LoanReportStatusBadge } from "@/components/portfolioAdmin/loanDetail/reports/LoanReportLedger";
import { ReportPdfFrame } from "@/components/portfolioAdmin/loanDetail/reports/ReportPdfFrame";
import { periodLabelForCode } from "./reportingFormat";

interface SeriesReportPreviewDrawerProps {
  report: LoanReportDto;
  seriesTitle: string;
  open: boolean;
  onClose: () => void;
  canPrepare: boolean;
  canReview: boolean;
  onSubmit: (reportId: string) => Promise<unknown>;
  onCertify: (reportId: string) => Promise<unknown>;
  onFile: (reportId: string) => Promise<unknown>;
  isPending: boolean;
}

export function SeriesReportPreviewDrawer({
  report,
  seriesTitle,
  open,
  onClose,
  canPrepare,
  canReview,
  onSubmit,
  onCertify,
  onFile,
  isPending,
}: SeriesReportPreviewDrawerProps) {
  const toast = useToast();
  const [actionError, setActionError] = useState<string | null>(null);
  // Submit/Certify/File stay disabled until the PDF preview has actually
  // rendered — nobody signs off a report whose preview never appeared.
  const [previewReady, setPreviewReady] = useState(false);

  const run = async (fn: () => Promise<unknown>, successMessage: string) => {
    setActionError(null);
    try {
      await fn();
      toast.success(successMessage);
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  };

  const footer = (
    <>
      {actionError && (
        <p
          className="text-caption text-status-error m-0 mr-auto"
          role="alert"
          data-testid="series-report-action-error"
        >
          {actionError}
        </p>
      )}
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
      {report.status === "Draft" && canPrepare && (
        <Button
          variant="primary"
          onClick={() => void run(() => onSubmit(report.id), "Submitted for review")}
          loading={isPending}
          disabled={!previewReady}
          data-testid="series-report-submit"
        >
          Submit for review
        </Button>
      )}
      {report.status === "PendingReview" && canReview && (
        <Button
          variant="primary"
          onClick={() =>
            void run(async () => {
              await onCertify(report.id);
              await onFile(report.id);
            }, "Report certified and filed")
          }
          loading={isPending}
          disabled={!previewReady}
          data-testid="series-report-certify-file"
        >
          Certify &amp; file
        </Button>
      )}
      {report.status === "Certified" && canReview && (
        <Button
          variant="primary"
          onClick={() => void run(() => onFile(report.id), "Report filed")}
          loading={isPending}
          disabled={!previewReady}
          data-testid="series-report-file"
        >
          File
        </Button>
      )}
      {report.status === "Filed" && (
        <span className="inline-flex items-center gap-space-3 text-small text-gray-mid">
          Filed {formatDate(report.filedAt)}
          {report.filedDocumentId && report.loanId && (
            <Link
              to={`/portfolio-admin/loans/${report.loanId}?tab=documents`}
              className="text-gold-shadow hover:underline underline-offset-4"
              data-testid="series-report-filed-document-link"
            >
              View document
            </Link>
          )}
        </span>
      )}
    </>
  );

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      width="xl"
      title={`${report.seriesCode ?? ""} ${seriesTitle}`.trim()}
      subtitle={
        report.seriesCode
          ? periodLabelForCode(report.seriesCode, report.periodEnd)
          : undefined
      }
      footer={footer}
      testId="series-report-preview-drawer"
    >
      <div className="flex h-full flex-col gap-space-3">
        <div className="flex flex-wrap items-center gap-space-3">
          <LoanReportStatusBadge status={report.status} />
          <span className="text-caption text-gray-mid">
            Prepared by {report.preparedByUserName ?? "—"} ·{" "}
            {formatDate(report.preparedAt)}
          </span>
          {report.certifiedAt && (
            <span className="text-caption text-gray-mid">
              Reviewed by {report.certifiedByUserName ?? "—"} ·{" "}
              {formatDate(report.certifiedAt)}
            </span>
          )}
          <span className="text-caption text-gray-mid">
            Computed from platform data
          </span>
        </div>
        <ReportPdfFrame
          src={loanReportsApi.previewUrl(report.id)}
          onReadyChange={setPreviewReady}
          testId="series-report-preview-iframe"
        />
      </div>
    </ResourceDrawer>
  );
}
